import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Eye,
  EyeOff,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";
import Navbar from "../components/nav/Navbar";
import SiteFooter from "../components/SiteFooter";

const USER_API = process.env.REACT_APP_USER_SERVICE_URL;

/**
 * ResetPasswordPage
 *
 * Reached via: /reset-password?token=<hex>
 *
 * Flow:
 *   1. Extract token from URL query param
 *   2. User enters new password + confirmation
 *   3. POST to /api/auth/reset-password with { token, new_password }
 *   4. On success → show confirmation → redirect to /auth after 3s
 *   5. On failure → show error (expired/invalid token)
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [noToken, setNoToken] = useState(false);

  // If no token in URL, show error immediately
  useEffect(() => {
    if (!token) setNoToken(true);
  }, [token]);

  // Redirect to auth after successful reset
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => navigate("/auth", { replace: true }), 3000);
    return () => clearTimeout(t);
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 10) {
      setError("Password must be at least 10 characters");
      return;
    }
    if (!/[A-Za-z]/.test(password)) {
      setError("Password must contain at least one letter");
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError("Password must contain at least one number");
      return;
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      setError("Password must contain at least one symbol");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${USER_API}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "Unable to reset password. The link may have expired.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 px-4 rounded-xl font-body text-sm focus:outline-none focus:ring-2 transition-shadow";
  const inputStyle = {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "hsl(var(--foreground))",
    "--tw-ring-color": "var(--vb-blue)",
  };

  return (
    <>
      <Navbar />
      <main
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          paddingTop: "calc(var(--header-h, 80px) + 40px)",
          paddingBottom: 60,
        }}
      >
        <div className="w-full max-w-md">
          <div
            className="rounded-3xl border bg-card p-8 md:p-10"
            style={{
              borderColor: "rgba(255,255,255,0.08)",
              boxShadow: "var(--glow-card)",
            }}
          >
            {/* ── No token state ── */}
            {noToken && (
              <div className="flex flex-col items-center text-center gap-5 py-4">
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: "rgba(239,68,68,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AlertCircle className="h-7 w-7" style={{ color: "#f87171" }} />
                </div>
                <div>
                  <h1
                    style={{
                      fontFamily: "'Oswald', sans-serif",
                      fontWeight: 700,
                      fontSize: 24,
                      color: "hsl(var(--foreground))",
                      marginBottom: 8,
                    }}
                  >
                    Invalid Reset Link
                  </h1>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    This password reset link is missing or malformed. Please
                    request a new one.
                  </p>
                </div>
                <Link
                  to="/forgot-password"
                  className="btn-primary justify-center"
                  style={{
                    height: 46,
                    borderRadius: 12,
                    fontSize: 14,
                    textDecoration: "none",
                    width: "100%",
                    display: "flex",
                  }}
                >
                  Request New Link
                </Link>
              </div>
            )}

            {/* ── Success state ── */}
            {success && (
              <div className="flex flex-col items-center text-center gap-5 py-4">
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: "rgba(91,140,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircle2
                    className="h-7 w-7"
                    style={{ color: "var(--vb-blue)" }}
                  />
                </div>
                <div>
                  <h1
                    style={{
                      fontFamily: "'Oswald', sans-serif",
                      fontWeight: 700,
                      fontSize: 24,
                      color: "hsl(var(--foreground))",
                      marginBottom: 8,
                    }}
                  >
                    Password Reset
                  </h1>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    Your password has been updated. Redirecting you to sign
                    in...
                  </p>
                </div>
                <Link
                  to="/auth"
                  className="font-body text-sm font-semibold flex items-center gap-1.5"
                  style={{ color: "var(--vb-blue)", textDecoration: "none" }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Sign in now
                </Link>
              </div>
            )}

            {/* ── Password form ── */}
            {!noToken && !success && (
              <>
                <div className="text-center mb-8">
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 14,
                      background: "rgba(91,140,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                    }}
                  >
                    <ShieldCheck
                      className="h-6 w-6"
                      style={{ color: "var(--vb-blue)" }}
                    />
                  </div>
                  <h1
                    style={{
                      fontFamily: "'Oswald', sans-serif",
                      fontWeight: 700,
                      fontSize: 28,
                      color: "hsl(var(--foreground))",
                      marginBottom: 6,
                    }}
                  >
                    Set New Password
                  </h1>
                  <p className="font-body text-sm text-muted-foreground">
                    Choose a strong password for your account
                  </p>
                </div>

                {error && (
                  <div
                    className="mb-5 px-4 py-3 rounded-xl text-sm font-body"
                    style={{
                      background: "rgba(239,68,68,0.08)",
                      color: "#f87171",
                      border: "1px solid rgba(239,68,68,0.2)",
                    }}
                  >
                    {error}
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label className="font-body text-xs font-medium text-muted-foreground mb-1.5 block">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPw ? "text" : "password"}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Min. 10 chars, 1 letter, 1 number, 1 symbol"
                        className={`${inputCls} pr-11`}
                        style={inputStyle}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw(!showPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                        }}
                      >
                        {showPw ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="font-body text-xs font-medium text-muted-foreground mb-1.5 block">
                      Confirm Password
                    </label>
                    <input
                      type={showPw ? "text" : "password"}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter password"
                      className={inputCls}
                      style={inputStyle}
                    />
                  </div>

                  {/* Password strength hints */}
                  <div
                    className="flex flex-wrap gap-2"
                    style={{ marginTop: -4 }}
                  >
                    {[
                      { ok: password.length >= 10, label: "10+ chars" },
                      { ok: /[A-Za-z]/.test(password), label: "Letter" },
                      { ok: /[0-9]/.test(password), label: "Number" },
                      { ok: /[^A-Za-z0-9]/.test(password), label: "Symbol" },
                      {
                        ok:
                          password.length > 0 &&
                          confirmPassword.length > 0 &&
                          password === confirmPassword,
                        label: "Match",
                      },
                    ].map(({ ok, label }) => (
                      <span
                        key={label}
                        className="font-body text-[10px] font-medium flex items-center gap-1"
                        style={{
                          color: ok
                            ? "var(--vb-blue)"
                            : "hsl(var(--muted-foreground))",
                          opacity: ok ? 1 : 0.5,
                          transition: "color 0.15s, opacity 0.15s",
                        }}
                      >
                        <span style={{ fontSize: 9 }}>
                          {ok ? "✔" : "○"}
                        </span>
                        {label}
                      </span>
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center"
                    style={{ height: 50, borderRadius: 12, fontSize: 15 }}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Reset Password"
                    )}
                  </button>
                </form>

                <div className="text-center mt-6">
                  <Link
                    to="/auth"
                    className="font-body text-sm font-medium flex items-center justify-center gap-1.5"
                    style={{
                      color: "var(--vb-blue)",
                      textDecoration: "none",
                    }}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to sign in
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </>
  );
}
